import { CONSENT_TEXT } from "../components/SubscribeForm";
import { PRIVACY_CONTACT, PRIVACY_OPERATOR } from "../lib/legal";
import { StaticNote, StaticPage, StaticSection } from "./StaticPage";

/**
 * The Privacy notice (`WEB-019`).
 *
 * Every factual claim here was read from `dealbid-core`'s migrations and source rather than
 * from a template, and the wording was approved by the operator. That matters more than it
 * sounds: a generated privacy policy describes collection a product does not do, and Dealbid
 * stores *less* than a generic notice would claim — no third-party analytics at all, no raw IP
 * addresses, and Google scopes limited to identity. Describing more than is true would be both
 * a lie and a worse page.
 *
 * Three claims are load-bearing and each is checked by a test in `test/sections/privacy.test.tsx`,
 * because they are the ones a later edit would most plausibly soften:
 *
 *   1. raw IP addresses and user agents are **never stored** — `src/routes/click.ts` hashes
 *      them with a secret salt and the UTC date and keeps only the digest;
 *   2. the Google scopes are `openid`, `email` and `profile` and nothing else, so Dealbid
 *      cannot reach Gmail, Drive, contacts or act anywhere on a founder's behalf;
 *   3. there is no advertising and no third-party tracking script of any kind.
 *
 * The subscription consent sentence is imported rather than retyped: `dealbid-core` stores it
 * verbatim with every subscriber (`subscribers.consent_text`), `SubscribeForm` already holds
 * the copy this repository shows, and a page that quoted a different sentence would make live
 * consent records describe something this site denies.
 */
export function PrivacyNotice() {
  return (
    <StaticPage
      kicker="Policy"
      title="Privacy"
      documentTitle="Privacy — Dealbid"
      lede={
        <>
          Dealbid is a marketplace for software deals. Buyers browse without an account.
          Founders sign in to list a deal and bid for placement. This page says what is stored
          for each, and what is not.
        </>
      }
    >
      <StaticSection id="browsing" title="If you are just browsing">
        <p>
          <strong>Dealbid does not know who you are.</strong> Browsing creates no account, and
          no name, email address or IP address is stored for you.
        </p>
        <p>
          When you click through to a deal, that click is recorded so the founder can see their
          own click figure, and so the weekly and monthly lists — which rank on buyer clicks,
          never on money — can be counted. What is stored is a <strong>pseudonym</strong>, not
          you:
        </p>
        <ul>
          <li>
            Your IP address and browser user-agent are used to calculate it, together with a
            secret and the current date. <strong>Neither is stored.</strong>
          </li>
          <li>
            The date is part of the calculation, so the pseudonym changes every day. The record
            cannot be used to follow one person from one day to the next.
          </li>
          <li>
            The secret means a copy of the table cannot be worked backwards into a list of IP
            addresses.
          </li>
        </ul>
        <p>
          <strong>There is no analytics and no advertising on this site.</strong> No Google
          Analytics, no tag manager, no pixel, and no third-party script of any kind.
        </p>
        <p>
          <strong>One cookie</strong>, and only if you choose a colour theme. It stores{" "}
          <code>light</code>, <code>dark</code> or nothing, so the site does not show you the
          wrong theme on the next page. It identifies nobody.
        </p>
      </StaticSection>

      <StaticSection id="subscribers" title="If you subscribe to deal emails">
        <p>
          Your email address is stored, with the exact sentence you agreed to and the time you
          agreed to it:
        </p>
        <blockquote>{CONSENT_TEXT}</blockquote>
        <p>
          The wording itself is stored rather than a tick, so what you agreed to can always be
          shown back to you — even after the wording changes for later subscribers. You can
          unsubscribe at any time, and the date you did is recorded.
        </p>
      </StaticSection>

      <StaticSection id="founders" title="If you sign in as a founder">
        <p>Sign-in is Google. Dealbid never sees or stores a password.</p>
        <p>
          <strong>What Google provides, and what is kept:</strong> your Google account
          identifier, your email address and your display name.
        </p>
        <StaticNote title="The permissions Dealbid asks Google for">
          <p>
            <code>openid</code>, <code>email</code> and <code>profile</code> —{" "}
            <strong>and nothing else</strong>. Dealbid has no access to your Gmail, Drive,
            Calendar, contacts or any other Google service, and cannot act on your behalf
            anywhere in Google. It asks for identity, and nothing more.
          </p>
        </StaticNote>
        <p>
          While you are signed in, a session record is held. The session identifier is stored
          only as a one-way digest, so a copy of the database does not let anyone sign in as
          you. When the session was created, last used and ended is recorded.
        </p>
        <p>
          What you provide as a founder is also stored: your display name and contact email,
          the deals you submit, your bids, your promotional credit ledger, and the moderation
          decisions taken on your deals along with the note explaining each one.
        </p>
      </StaticSection>

      <StaticSection id="reports" title="If you report a deal">
        <p>
          Reporting is anonymous unless you choose otherwise. If you give an email address so
          Dealbid can follow up, it is stored with the report. If you do not, the report is
          stored without one.
        </p>
      </StaticSection>

      <StaticSection id="processors" title="Who else handles this data">
        <ul>
          <li>
            <strong>Cloudflare</strong> hosts Dealbid and stores its database.
          </li>
          <li>
            <strong>Google</strong> authenticates founders, and only founders.
          </li>
        </ul>
        <p>
          That is the complete list. <strong>Your data is not sold, and it is not shared for
          advertising.</strong>
        </p>
      </StaticSection>

      <StaticSection id="payments" title="Payments">
        <p>
          Dealbid currently runs on promotional credit and takes no card payments, so{" "}
          <strong>no card or bank details are held at all</strong>. If that changes, this page
          changes with it before any payment is taken.
        </p>
      </StaticSection>

      <StaticSection id="retention" title="How long things are kept">
        <ul>
          <li>
            <strong>Subscriber addresses</strong> — until you unsubscribe. After that a minimal
            record that you unsubscribed is kept, so you are not emailed again.
          </li>
          <li>
            <strong>Founder accounts and the Google identity attached to them</strong> — while
            the account is in use, and deleted 24 months after the last sign-in, or sooner on
            request.
          </li>
          <li>
            <strong>Deals, bids and credit records</strong> — kept as business records even
            after an account is closed. They refer to one another, and a marketplace cannot
            un-record an auction that happened.
          </li>
          <li>
            <strong>Click pseudonyms</strong> — kept indefinitely. Each founder&rsquo;s lifetime
            click figure is counted from them, and the records identify nobody: the raw IP and
            user-agent were never stored, and the pseudonym changes daily.
          </li>
          <li>
            <strong>Moderation decisions</strong> — kept while the account exists, because they
            are the founder&rsquo;s own record of what was decided about their deal.
          </li>
          <li>
            <strong>Administrative logs</strong> — 24 months.
          </li>
        </ul>
      </StaticSection>

      <StaticSection id="your-choices" title="Your choices">
        <p>
          Email <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a> and Dealbid will
          respond within 30 days. You can ask what is held about you, ask for it to be
          corrected, or ask for it to be deleted.
        </p>
        <p>
          If you are a founder, erasing your account removes your name, your address and the
          Google account attached to it. What stays is what the marketplace did: the deals,
          bids, credit and placements that record auctions which actually happened. Those no
          longer name you.
        </p>
        <p>
          If you are a subscriber, erasing you removes your record entirely. That is different
          from unsubscribing, which keeps your address precisely so you are not emailed again
          — so if you ask to be erased and later subscribe once more, that is a fresh consent
          and it is recorded fresh.
        </p>
      </StaticSection>

      <StaticSection id="who-we-are" title="Who Dealbid is, and which law applies">
        <p>
          Dealbid is operated by {PRIVACY_OPERATOR.name}
          {PRIVACY_OPERATOR.address === null ? null : <>, {PRIVACY_OPERATOR.address}</>} — an
          individual, not a company. Questions about this page reach a person, not a
          department.
        </p>
        <p>
          This notice is governed by the law of {PRIVACY_OPERATOR.jurisdiction}.{" "}
          <strong>
            That does not remove any consumer or data-protection right you have under the law
            of the country you live in.
          </strong>{" "}
          Where the two differ, the protection that is stronger for you is the one that
          applies.
        </p>
        <p>
          Dealbid treats the UK and EU General Data Protection Regulations as applying to it.
          The lawful basis is your <strong>consent</strong> for deal emails, the{" "}
          <strong>contract</strong> between you and Dealbid for a founder account and the
          placements it buys, and <strong>legitimate interest</strong> for the anti-abuse
          filtering behind click counting — which is the reason a click is stored as a rotating
          pseudonym rather than as an address.
        </p>
      </StaticSection>

      <StaticSection id="changes" title="Changes to this page">
        <p>
          When this page changes, the date below changes with it. There is no mailing list for
          policy changes and Dealbid does not promise to notify you of one.
        </p>
        <p>
          <strong>Last updated:</strong> {PRIVACY_OPERATOR.lastUpdated}
        </p>
      </StaticSection>
    </StaticPage>
  );
}
